(() => {
  let collectionRequestController;

  const publishLabEvent = (name, detail = {}) => {
    const eventName = `fivem_lab_${name}`;
    const payload = { ...detail, pagePath: window.location.pathname };

    try {
      const result = window.Shopify?.analytics?.publish?.(eventName, payload);
      if (result?.catch) result.catch(() => {});
    } catch (error) {
      console.warn(`Could not publish ${eventName}`, error);
    }

    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: eventName, ...payload });
    document.dispatchEvent(new CustomEvent(`lab:${name}`, { detail: payload }));
  };

  const preserveThemePreviewParams = (url) => {
    const current = new URL(window.location.href);
    ['preview_theme_id', '_fd', 'pb'].forEach((name) => {
      if (!url.searchParams.has(name) && current.searchParams.has(name)) {
        url.searchParams.set(name, current.searchParams.get(name));
      }
    });
    return url;
  };

  const AD_IMPRESSIONS_KEY = 'fivem_lab_ad_impressions_v1';
  const adPageImpressions = new Set();
  let adObserver;

  const readAdImpressions = () => {
    try {
      const value = JSON.parse(window.localStorage.getItem(AD_IMPRESSIONS_KEY) || '{}');
      return value && typeof value === 'object' ? value : {};
    } catch (error) {
      return {};
    }
  };

  const writeAdImpressions = (value) => {
    try {
      window.localStorage.setItem(AD_IMPRESSIONS_KEY, JSON.stringify(value));
    } catch (error) {
      // Campaigns still work when storage is unavailable.
    }
  };

  const parseCampaignDate = (value, endOfDay = false) => {
    const source = String(value || '').trim();
    if (!source) return Number.NaN;
    const normalized = /^\d{4}-\d{2}-\d{2}$/.test(source)
      ? `${source}T${endOfDay ? '23:59:59.999' : '00:00:00'}`
      : source;
    return Date.parse(normalized);
  };

  const syncAdGroups = (scope = document) => {
    const groups = new Set();
    if (scope.matches?.('[data-lab-ad-group]')) groups.add(scope);
    scope.querySelectorAll?.('[data-lab-ad-group]').forEach((group) => groups.add(group));
    scope.closest?.('[data-lab-ad-group]') && groups.add(scope.closest('[data-lab-ad-group]'));
    groups.forEach((group) => {
      const visibleAds = [...group.querySelectorAll('[data-lab-ad-campaign]')].filter((ad) => !ad.hidden);
      group.hidden = visibleAds.length === 0;
    });
  };

  const recordAdImpression = (ad) => {
    const campaign = String(ad.dataset.labAdCampaign || '').trim();
    if (!campaign || adPageImpressions.has(campaign)) return;
    adPageImpressions.add(campaign);

    const day = new Date().toISOString().slice(0, 10);
    const impressions = readAdImpressions();
    const previous = impressions[campaign];
    impressions[campaign] = {
      day,
      count: previous?.day === day ? Number(previous.count || 0) + 1 : 1
    };
    writeAdImpressions(impressions);
    publishLabEvent('ad_impression', {
      campaign,
      kind: ad.dataset.labAdKind || 'placement',
      placement: ad.dataset.labAdPlacement || ''
    });
  };

  const initAdCampaigns = (scope = document) => {
    const candidates = [];
    if (scope.matches?.('[data-lab-ad-campaign]')) candidates.push(scope);
    scope.querySelectorAll?.('[data-lab-ad-campaign]').forEach((ad) => candidates.push(ad));
    const now = Date.now();
    const day = new Date().toISOString().slice(0, 10);
    const impressions = readAdImpressions();
    const designMode = Boolean(window.Shopify?.designMode);

    candidates.forEach((ad) => {
      const campaign = String(ad.dataset.labAdCampaign || '').trim();
      const startsAt = parseCampaignDate(ad.dataset.labAdStart);
      const endsAt = parseCampaignDate(ad.dataset.labAdEnd, true);
      const cap = Math.max(1, Number(ad.dataset.labAdFrequency) || 3);
      const stored = impressions[campaign];
      const overFrequency = stored?.day === day && Number(stored.count || 0) >= cap;
      const outsideSchedule = (Number.isFinite(startsAt) && now < startsAt)
        || (Number.isFinite(endsAt) && now > endsAt);

      ad.hidden = !designMode && (outsideSchedule || overFrequency);
      ad.dataset.labAdReady = 'true';
      if (ad.hidden) return;

      if (!adObserver && 'IntersectionObserver' in window) {
        adObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.45) return;
            recordAdImpression(entry.target);
            adObserver.unobserve(entry.target);
          });
        }, { threshold: [0.45] });
      }

      if (adObserver) adObserver.observe(ad);
      else recordAdImpression(ad);
    });

    syncAdGroups(scope);
  };

  document.querySelectorAll('[data-lab-contact-form]').forEach((form) => {
    const params = new URLSearchParams(window.location.search);
    const resource = params.get('resource');
    const issue = params.get('issue');
    const requestedTopic = params.get('topic');
    const topic = form.querySelector('[data-lab-contact-topic]');
    const resourceInput = form.querySelector('[data-lab-contact-resource]');
    const body = form.querySelector('[data-lab-contact-body]');

    if (issue === 'listing' && topic && !topic.value) {
      topic.value = 'Report incorrect information';
    }

    if (requestedTopic && topic) {
      const matchingOption = [...topic.options].find((option) => option.value.toLowerCase() === requestedTopic.toLowerCase());
      if (matchingOption) topic.value = matchingOption.value;
    }

    if (resource && resourceInput && !resourceInput.value) {
      const resourceUrl = /^https?:\/\//i.test(resource)
        ? resource
        : `${window.location.origin}/products/${encodeURIComponent(resource)}`;
      resourceInput.value = resourceUrl;
      if (body && !body.value.trim()) body.value = `I found a problem with this listing:\n${resourceUrl}\n\n`;
    }
  });

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-lab-outbound-resource]');
    if (!link) return;

    const detail = {
      handle: link.dataset.labOutboundResource || '',
      destination: link.href,
      location: link.closest('[data-lab-product-page]') ? 'product' : 'catalog'
    };
    publishLabEvent('outbound_resource', detail);
  });

  document.addEventListener('click', async (event) => {
    const coupon = event.target.closest('[data-lab-coupon-copy]');
    if (coupon) {
      event.preventDefault();
      const code = coupon.dataset.labCouponCopy || '';
      const status = coupon.querySelector('[data-lab-coupon-status]');
      try {
        await navigator.clipboard.writeText(code);
      } catch (error) {
        const input = document.createElement('input');
        input.value = code;
        document.body.append(input);
        input.select();
        document.execCommand('copy');
        input.remove();
      }
      if (status) {
        status.textContent = 'Copied';
        window.setTimeout(() => { status.textContent = 'Copy'; }, 1800);
      }
      const campaignRoot = coupon.closest('[data-lab-ad-campaign]');
      publishLabEvent('coupon_copy', {
        campaign: campaignRoot?.dataset.labAdCampaign || '',
        code,
        placement: campaignRoot?.dataset.labAdPlacement || ''
      });
      return;
    }

    const adLink = event.target.closest('[data-lab-ad-click]');
    const campaignRoot = adLink?.closest('[data-lab-ad-campaign]');
    if (!adLink || !campaignRoot) return;
    const detail = {
      campaign: campaignRoot.dataset.labAdCampaign || '',
      kind: campaignRoot.dataset.labAdKind || 'placement',
      placement: campaignRoot.dataset.labAdPlacement || '',
      destination: adLink.href || ''
    };
    publishLabEvent('ad_click', detail);
    publishLabEvent(detail.kind === 'affiliate' ? 'affiliate_click' : 'sponsored_product_click', detail);
  });

  const buildFormUrl = (form) => {
    const url = preserveThemePreviewParams(new URL(form.action || window.location.href, window.location.href));
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form).entries()) {
      if (String(value).trim() !== '') params.append(key, value);
    }
    ['preview_theme_id', '_fd', 'pb'].forEach((name) => {
      if (url.searchParams.has(name)) params.set(name, url.searchParams.get(name));
    });
    url.search = params.toString();
    return url;
  };

  const setMobileFiltersOpen = (open) => {
    const root = document.querySelector('[data-lab-filter-root]');
    const toggle = root?.querySelector('[data-lab-mobile-filter-toggle]');
    const drawer = root?.querySelector('[data-lab-filter-drawer]');
    const backdrop = root?.querySelector('[data-lab-mobile-filter-backdrop]');
    if (!root || !drawer) return;

    const wasOpen = document.body.classList.contains('lab-filter-drawer-open');
    document.body.classList.toggle('lab-filter-drawer-open', open);
    toggle?.setAttribute('aria-expanded', String(open));
    backdrop?.toggleAttribute('hidden', !open);
    if (open) drawer.querySelector('input, button, select, a')?.focus();
    else if (wasOpen) toggle?.focus({ preventScroll: true });
  };

  const loadCollectionResults = async (href, historyMode = 'push') => {
    const root = document.querySelector('[data-lab-collection-page]');
    const currentResults = root?.querySelector('[data-lab-collection-results]');
    if (!root || !currentResults) {
      window.location.assign(href);
      return;
    }

    const targetUrl = preserveThemePreviewParams(new URL(href, window.location.href));
    if (targetUrl.origin !== window.location.origin) {
      window.location.assign(targetUrl.toString());
      return;
    }

    collectionRequestController?.abort();
    const controller = new AbortController();
    collectionRequestController = controller;
    currentResults.style.minHeight = `${currentResults.offsetHeight}px`;
    currentResults.classList.add('is-loading');
    root.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(targetUrl.toString(), {
        signal: controller.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`Collection request failed with ${response.status}`);

      const nextDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextRoot = nextDocument.querySelector('[data-lab-collection-page]');
      const nextResults = nextRoot?.querySelector('[data-lab-collection-results]');
      if (!nextRoot || !nextResults) throw new Error('Collection results are missing from the response');

      const currentFilters = root.querySelector('.lab-filters-panel');
      const nextFilters = nextRoot.querySelector('.lab-filters-panel');
      if (currentFilters && nextFilters) currentFilters.replaceWith(nextFilters);

      const currentSubcategories = root.querySelector('.lab-subcategory-panel');
      const nextSubcategories = nextRoot.querySelector('.lab-subcategory-panel');
      if (currentSubcategories && nextSubcategories) currentSubcategories.replaceWith(nextSubcategories);

      const currentTotal = root.querySelector('[data-lab-collection-total]');
      const nextTotal = nextRoot.querySelector('[data-lab-collection-total]');
      if (currentTotal && nextTotal) currentTotal.textContent = nextTotal.textContent;

      const currentMobileSort = root.querySelector('[data-lab-mobile-sort]');
      const nextMobileSort = nextRoot.querySelector('[data-lab-mobile-sort]');
      if (currentMobileSort && nextMobileSort) currentMobileSort.value = nextMobileSort.value;

      const currentMobileFilterCount = root.querySelector('[data-lab-mobile-filter-count]');
      const nextMobileFilterCount = nextRoot.querySelector('[data-lab-mobile-filter-count]');
      if (currentMobileFilterCount && nextMobileFilterCount) {
        currentMobileFilterCount.textContent = nextMobileFilterCount.textContent;
        currentMobileFilterCount.toggleAttribute('hidden', nextMobileFilterCount.hasAttribute('hidden'));
      }

      nextResults.classList.add('is-entering');
      currentResults.replaceWith(nextResults);
      const finishEntrance = () => nextResults.classList.remove('is-entering');
      nextResults.addEventListener('animationend', finishEntrance, { once: true });
      window.setTimeout(finishEntrance, 450);
      updateSavedButtons(nextResults);
      initAdCampaigns(nextResults);
      document.title = nextDocument.title || document.title;
      setMobileFiltersOpen(false);

      if (historyMode === 'push') window.history.pushState({}, '', targetUrl.toString());
      if (historyMode === 'replace') window.history.replaceState({}, '', targetUrl.toString());

    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
      window.location.assign(targetUrl.toString());
    } finally {
      if (collectionRequestController === controller) {
        root.removeAttribute('aria-busy');
        const visibleResults = root.querySelector('[data-lab-collection-results]');
        visibleResults?.classList.remove('is-loading');
        if (visibleResults) visibleResults.style.minHeight = '';
      }
    }
  };

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-lab-filter-form], [data-lab-sort-form]');
    if (!form) return;
    event.preventDefault();
    const targetUrl = buildFormUrl(form);
    publishLabEvent('catalog_filter', {
      source: form.matches('[data-lab-sort-form]') ? 'sort' : 'filter',
      query: targetUrl.search
    });
    if (form.closest('[data-lab-collection-page]')) loadCollectionResults(targetUrl.toString());
    else window.location.assign(targetUrl.toString());
  });

  document.addEventListener('change', (event) => {
    const mobileSort = event.target.closest('[data-lab-mobile-sort]');
    if (mobileSort) {
      const targetUrl = preserveThemePreviewParams(new URL(window.location.href));
      targetUrl.searchParams.set('sort_by', mobileSort.value);
      targetUrl.searchParams.delete('page');
      loadCollectionResults(targetUrl.toString());
      return;
    }

    const sortSelect = event.target.closest('[data-lab-sort-form] select');
    if (sortSelect) {
      sortSelect.form.requestSubmit();
      return;
    }

    const filterCheckbox = event.target.closest('[data-lab-filter-form] input[type="checkbox"]');
    if (filterCheckbox && !window.matchMedia('(max-width: 620px)').matches) {
      filterCheckbox.form.requestSubmit();
    }
  });

  document.addEventListener('click', (event) => {
    const subcategoryToggle = event.target.closest('[data-lab-subcategory-toggle]');
    if (subcategoryToggle) {
      const panel = subcategoryToggle.closest('.lab-subcategory-panel');
      const expanded = !panel?.classList.contains('is-expanded');
      panel?.classList.toggle('is-expanded', expanded);
      subcategoryToggle.setAttribute('aria-expanded', String(expanded));
      const label = subcategoryToggle.querySelector('[data-lab-subcategory-toggle-label]');
      if (label) label.textContent = expanded ? 'Show less' : 'Show all';
      return;
    }

    if (event.target.closest('[data-lab-mobile-filter-toggle]')) {
      setMobileFiltersOpen(true);
      return;
    }

    if (event.target.closest('[data-lab-mobile-filter-close], [data-lab-mobile-filter-backdrop]')) {
      setMobileFiltersOpen(false);
      return;
    }

    const link = event.target.closest('[data-lab-subcategory-link], .lab-results__active a, [data-lab-collection-pagination] a, .lab-filters-panel__header a');
    if (!link || !link.closest('[data-lab-collection-page]')) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.target && link.target !== '_self') return;

    const targetUrl = new URL(link.href, window.location.href);
    if (targetUrl.origin !== window.location.origin) return;
    if (link.closest('[data-lab-resource-card]') || targetUrl.pathname.includes('/products/')) return;
    if (link.hasAttribute('data-lab-subcategory-link')) {
      const collectionRoot = link.closest('[data-lab-collection-page]');
      const baseUrl = new URL(collectionRoot.dataset.labCollectionUrl || window.location.pathname, window.location.href);
      const basePath = decodeURIComponent(baseUrl.pathname).replace(/\/$/, '');
      const targetPath = decodeURIComponent(targetUrl.pathname).replace(/\/$/, '');
      if (targetPath !== basePath && !targetPath.startsWith(`${basePath}/`)) return;
    }
    event.preventDefault();
    loadCollectionResults(targetUrl.toString());
  });

  window.addEventListener('popstate', () => {
    if (document.querySelector('[data-lab-collection-page]')) {
      loadCollectionResults(window.location.href, 'none');
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && document.body.classList.contains('lab-filter-drawer-open')) {
      setMobileFiltersOpen(false);
    }
  });

  document.querySelectorAll('[data-lab-localization-select]').forEach((select) => {
    select.addEventListener('change', () => select.form.submit());
  });

  const initPredictiveSearch = (root) => {
    const input = root.querySelector('input[type="search"]');
    const form = root.querySelector('form[role="search"]');
    const results = root.querySelector('[data-lab-predictive-results]');
    const closeButton = root.querySelector('[data-lab-search-command-close]');
    if (!input || !form || !results || root.dataset.labPredictiveReady === 'true') return;
    root.dataset.labPredictiveReady = 'true';

    const recentSearchKey = 'fivemLabRecentSearchesV1';
    const recentLimit = Math.max(3, Math.min(8, Number(root.dataset.labRecentSearchLimit) || 5));
    const isRussian = document.documentElement.lang.toLowerCase().startsWith('ru');
    const copy = (english, russian) => isRussian ? russian : english;
    const listenerController = new AbortController();
    const listenerOptions = { signal: listenerController.signal };
    let requestController;
    let debounceTimer;
    let resultLinks = [];
    let activeIndex = -1;
    let backdrop;
    let backdropRemovalTimer;
    let backdropFrame;
    let pointerFocusPending = false;
    let requestSequence = 0;

    const searchUrlFor = (query) => {
      const searchUrl = new URL(root.dataset.labSearchUrl || '/search', window.location.origin);
      searchUrl.searchParams.set('q', query);
      searchUrl.searchParams.set('type', 'product');
      return searchUrl.toString();
    };

    const readRecentSearches = () => {
      try {
        const value = JSON.parse(window.localStorage.getItem(recentSearchKey) || '[]');
        return Array.isArray(value) ? value.filter(Boolean).slice(0, recentLimit) : [];
      } catch (error) {
        return [];
      }
    };

    const rememberSearch = (query) => {
      const cleanQuery = String(query || '').trim();
      if (cleanQuery.length < 2) return;
      const recent = readRecentSearches().filter((item) => item.toLocaleLowerCase() !== cleanQuery.toLocaleLowerCase());
      try {
        window.localStorage.setItem(recentSearchKey, JSON.stringify([cleanQuery, ...recent].slice(0, recentLimit)));
      } catch (error) {
        // Search remains fully functional when storage is unavailable.
      }
    };

    const setCommandOpen = (open) => {
      const wasOpen = root.classList.contains('is-command-open');
      root.classList.toggle('is-command-open', open);
      if (open) document.body.classList.add('lab-search-command-open');
      else if (!document.querySelector('[data-lab-predictive-search].is-command-open')) document.body.classList.remove('lab-search-command-open');
      if (open && !wasOpen) publishLabEvent('search_command_opened');
      if (!open && wasOpen) publishLabEvent('search_command_closed');
      if (open) {
        window.clearTimeout(backdropRemovalTimer);
        if (backdropFrame) window.cancelAnimationFrame(backdropFrame);
        if (!backdrop) {
          backdrop = document.createElement('button');
          backdrop.type = 'button';
          backdrop.className = 'lab-search-command-backdrop';
          backdrop.setAttribute('aria-label', copy('Close search', 'Закрыть поиск'));
          backdrop.addEventListener('click', (event) => {
            event.preventDefault();
            closeSearch(true);
          }, listenerOptions);
          document.body.append(backdrop);
        }
        backdropFrame = window.requestAnimationFrame(() => {
          backdropFrame = undefined;
          if (root.classList.contains('is-command-open')) backdrop?.classList.add('is-visible');
        });
      } else {
        if (backdropFrame) {
          window.cancelAnimationFrame(backdropFrame);
          backdropFrame = undefined;
        }
        if (backdrop) {
          backdrop.classList.remove('is-visible');
          backdropRemovalTimer = window.setTimeout(() => {
            backdrop?.remove();
            backdrop = undefined;
          }, 220);
        }
      }
    };

    const cancelPendingSearch = () => {
      window.clearTimeout(debounceTimer);
      requestSequence += 1;
      requestController?.abort();
      requestController = undefined;
    };

    const closeSearch = (blurInput = false) => {
      cancelPendingSearch();
      setOpen(false);
      if (blurInput) input.blur();
    };

    const setOpen = (open) => {
      results.toggleAttribute('hidden', !open);
      input.setAttribute('aria-expanded', String(open));
      if (open) setCommandOpen(true);
      if (!open) {
        input.removeAttribute('aria-activedescendant');
        activeIndex = -1;
        resultLinks.forEach((link) => link.classList.remove('is-active'));
        setCommandOpen(false);
      }
    };

    const setActiveResult = (index) => {
      if (!resultLinks.length) return;
      activeIndex = (index + resultLinks.length) % resultLinks.length;
      resultLinks.forEach((link, resultIndex) => {
        const active = resultIndex === activeIndex;
        link.classList.toggle('is-active', active);
        if (active) {
          input.setAttribute('aria-activedescendant', link.id);
          link.scrollIntoView({ block: 'nearest' });
        }
      });
    };

    const createResult = (entry, kind, index) => {
      const link = document.createElement('a');
      link.className = 'lab-predictive-result';
      const entryTitle = entry.title || entry.text || entry.query || 'Untitled';
      link.href = entry.url || searchUrlFor(entryTitle);
      link.id = `LabPredictiveResult-${index}`;
      link.setAttribute('role', 'option');

      const featuredImage = entry.featured_image;
      const imageUrl = typeof featuredImage === 'string' ? featuredImage : featuredImage?.url;
      if (kind === 'Resources' && imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = featuredImage?.alt || entryTitle;
        image.loading = 'lazy';
        link.append(image);
      } else {
        const visual = document.createElement('span');
        visual.className = 'lab-predictive-result__visual';
        visual.textContent = kind === 'Studios' ? 'ST' : kind === 'Categories' ? 'CAT' : kind === 'Pages' ? 'PAGE' : kind === 'Suggestions' || kind === 'Recent' ? 'S' : 'RES';
        visual.setAttribute('aria-hidden', 'true');
        link.append(visual);
      }

      const copyNode = document.createElement('span');
      copyNode.className = 'lab-predictive-result__copy';
      const title = document.createElement('strong');
      const meta = document.createElement('small');
      title.textContent = entryTitle;
      meta.textContent = kind === 'Resources'
        ? [entry.vendor, entry.type || entry.product_type].filter(Boolean).join(' · ') || 'Resource'
        : kind === 'Studios' ? copy('Creator profile', 'Профиль студии')
          : kind === 'Categories' ? copy('Catalog category', 'Категория каталога')
            : kind === 'Suggestions' ? copy('Suggested query', 'Поисковая подсказка')
              : kind === 'Recent' ? copy('Recent search', 'Недавний поиск')
                : kind.slice(0, -1);
      copyNode.append(title, meta);
      link.append(copyNode);
      return link;
    };

    const renderRecentSearches = () => {
      const recent = readRecentSearches();
      const fragment = document.createDocumentFragment();
      if (recent.length) {
        const group = document.createElement('section');
        group.className = 'lab-predictive-group lab-predictive-group--recent';
        const header = document.createElement('div');
        header.className = 'lab-predictive-group__header';
        const heading = document.createElement('strong');
        heading.className = 'lab-predictive-group__title';
        heading.textContent = copy('Recent', 'Недавние');
        const clear = document.createElement('button');
        clear.type = 'button';
        clear.dataset.labClearRecentSearches = '';
        clear.textContent = copy('Clear', 'Очистить');
        header.append(heading, clear);
        group.append(header);
        recent.forEach((query, index) => group.append(createResult({ title: query, url: searchUrlFor(query) }, 'Recent', index)));
        fragment.append(group);
      } else {
        const introduction = document.createElement('div');
        introduction.className = 'lab-predictive-search__intro';
        introduction.innerHTML = `<strong>${copy('Search the whole catalog', 'Поиск по всему каталогу')}</strong><span>${copy('Find resources, studios and categories without leaving the page.', 'Находите ресурсы, студии и категории, не покидая страницу.')}</span>`;
        fragment.append(introduction);
      }
      results.replaceChildren(fragment);
      resultLinks = [...results.querySelectorAll('.lab-predictive-result')];
      results.removeAttribute('aria-busy');
      setOpen(true);
    };

    const renderSearchFailure = (query) => {
      const recovery = document.createElement('section');
      recovery.className = 'lab-predictive-group lab-predictive-group--recovery';
      const message = document.createElement('p');
      message.className = 'lab-predictive-search__empty';
      message.textContent = copy('Live suggestions are temporarily unavailable.', 'Живые подсказки временно недоступны.');
      const link = document.createElement('a');
      link.className = 'lab-predictive-search__all';
      link.href = searchUrlFor(query);
      link.textContent = copy(`Search for “${query}”`, `Найти «${query}»`);
      recovery.append(message, link);
      results.replaceChildren(recovery);
      resultLinks = [link];
      results.removeAttribute('aria-busy');
      setOpen(true);
    };

    const renderResults = (payload, query) => {
      const data = payload?.resources?.results || {};
      const products = Array.isArray(data.products) ? data.products.slice(0, 6) : [];
      const studioNames = new Map();
      products.forEach((entry) => {
        const vendor = String(entry.vendor || '').trim();
        if (vendor && !studioNames.has(vendor.toLocaleLowerCase())) studioNames.set(vendor.toLocaleLowerCase(), vendor);
      });
      const studiosUrl = new URL(root.dataset.labStudiosUrl || '/collections/vendors', window.location.origin);
      const studios = [...studioNames.values()].slice(0, 4).map((name) => {
        const url = new URL(studiosUrl);
        url.searchParams.set('q', name);
        return { title: name, url: url.toString() };
      });
      const pages = Array.isArray(data.pages) ? data.pages.slice(0, 3) : [];
      const studioPages = pages.filter((entry) => String(entry.url || '').includes('/pages/studio-'));
      studioPages.forEach((entry) => {
        if (!studios.some((studio) => studio.title.toLocaleLowerCase() === String(entry.title || '').toLocaleLowerCase())) studios.unshift(entry);
      });
      const generalPages = pages.filter((entry) => !studioPages.includes(entry));
      const queries = Array.isArray(data.queries) ? data.queries.slice(0, 3).map((entry) => {
        const title = typeof entry === 'string' ? entry : entry.text || entry.styled_text || entry.title || '';
        return { title, url: searchUrlFor(title) };
      }).filter((entry) => entry.title) : [];
      const groups = [
        [copy('Resources', 'Ресурсы'), products, 'Resources'],
        [copy('Studios', 'Студии'), studios.slice(0, 4), 'Studios'],
        [copy('Categories', 'Категории'), Array.isArray(data.collections) ? data.collections.slice(0, 4) : [], 'Categories'],
        [copy('Suggestions', 'Подсказки'), queries, 'Suggestions'],
        [copy('Pages', 'Страницы'), generalPages, 'Pages']
      ];
      const fragment = document.createDocumentFragment();
      let resultIndex = 0;

      groups.forEach(([headingText, entries, kind]) => {
        if (!entries.length) return;
        const group = document.createElement('section');
        group.className = `lab-predictive-group lab-predictive-group--${kind.toLocaleLowerCase()}`;
        const heading = document.createElement('strong');
        heading.className = 'lab-predictive-group__title';
        heading.textContent = headingText;
        group.append(heading);
        entries.forEach((entry) => {
          group.append(createResult(entry, kind, resultIndex));
          resultIndex += 1;
        });
        fragment.append(group);
      });

      if (resultIndex === 0) {
        const empty = document.createElement('p');
        empty.className = 'lab-predictive-search__empty';
        empty.textContent = copy(`No resources found for “${query}”.`, `По запросу «${query}» ничего не найдено.`);
        fragment.append(empty);
      }

      const viewAll = document.createElement('a');
      viewAll.className = 'lab-predictive-search__all';
      viewAll.href = searchUrlFor(query);
      viewAll.textContent = copy(`View all results for “${query}”`, `Все результаты по запросу «${query}»`);
      fragment.append(viewAll);

      results.replaceChildren(fragment);
      resultLinks = [...results.querySelectorAll('.lab-predictive-result, .lab-predictive-search__all')];
      resultLinks.forEach((link, index) => {
        link.id ||= `LabPredictiveResult-${index}`;
        link.addEventListener('pointerenter', () => setActiveResult(index));
      });
      results.removeAttribute('aria-busy');
      setOpen(true);
    };

    const requestSuggestions = async (query) => {
      requestController?.abort();
      const controller = new AbortController();
      requestController = controller;
      const requestId = ++requestSequence;
      const endpoint = new URL(root.dataset.labPredictiveUrl || '/search/suggest.json', window.location.origin);
      endpoint.searchParams.set('q', query);
      endpoint.searchParams.set('resources[type]', 'product,collection,page,query');
      endpoint.searchParams.set('resources[limit]', '6');
      endpoint.searchParams.set('resources[options][unavailable_products]', 'hide');
      endpoint.searchParams.set('resources[options][fields]', 'title,product_type,vendor,tag');

      results.replaceChildren();
      const status = document.createElement('p');
      status.className = 'lab-predictive-search__status';
      status.textContent = copy('Searching the catalog...', 'Ищем по каталогу...');
      results.append(status);
      results.setAttribute('aria-busy', 'true');
      setOpen(true);

      try {
        const response = await fetch(endpoint.toString(), { signal: controller.signal });
        if (!response.ok) throw new Error(`Predictive search failed with ${response.status}`);
        const payload = await response.json();
        if (requestId !== requestSequence || !root.classList.contains('is-command-open')) return;
        renderResults(payload, query);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error(error);
        if (requestId === requestSequence && root.classList.contains('is-command-open')) renderSearchFailure(query);
      } finally {
        if (requestId === requestSequence) requestController = undefined;
      }
    };

    const openSearchForInput = () => {
      const query = input.value.trim();
      if (query.length >= 2) requestSuggestions(query);
      else renderRecentSearches();
    };

    input.addEventListener('input', () => {
      window.clearTimeout(debounceTimer);
      const query = input.value.trim();
      if (query.length < 2) {
        cancelPendingSearch();
        renderRecentSearches();
        return;
      }
      debounceTimer = window.setTimeout(() => requestSuggestions(query), 180);
    }, listenerOptions);

    input.addEventListener('pointerdown', () => {
      pointerFocusPending = true;
    }, listenerOptions);
    input.addEventListener('pointercancel', () => {
      pointerFocusPending = false;
    }, listenerOptions);
    input.addEventListener('blur', () => {
      pointerFocusPending = false;
    }, listenerOptions);
    input.addEventListener('focus', () => {
      if (!pointerFocusPending) openSearchForInput();
    }, listenerOptions);
    input.addEventListener('click', () => {
      pointerFocusPending = false;
      if (!root.classList.contains('is-command-open')) openSearchForInput();
    }, listenerOptions);

    closeButton?.addEventListener('click', () => {
      if (input.value) {
        input.value = '';
        cancelPendingSearch();
        renderRecentSearches();
        input.focus();
      } else {
        closeSearch(true);
      }
    }, listenerOptions);

    input.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' && !results.hidden) {
        event.preventDefault();
        setActiveResult(activeIndex + 1);
      } else if (event.key === 'ArrowUp' && !results.hidden) {
        event.preventDefault();
        setActiveResult(activeIndex - 1);
      } else if (event.key === 'Enter' && activeIndex >= 0 && resultLinks[activeIndex]) {
        event.preventDefault();
        window.location.assign(resultLinks[activeIndex].href);
      } else if (event.key === 'Escape') {
        closeSearch(true);
      }
    }, listenerOptions);

    form.addEventListener('submit', () => {
      const query = input.value.trim();
      rememberSearch(query);
      publishLabEvent('search', { query });
      closeSearch();
    }, listenerOptions);
    results.addEventListener('click', (event) => {
      const clear = event.target.closest('[data-lab-clear-recent-searches]');
      if (clear) {
        try {
          window.localStorage.removeItem(recentSearchKey);
        } catch (error) {
          // The panel can still reset visually when storage is blocked.
        }
        renderRecentSearches();
        return;
      }
      const link = event.target.closest('a');
      if (link) {
        const query = input.value.trim() || link.querySelector('strong')?.textContent || '';
        rememberSearch(query);
        publishLabEvent('search_result_click', { query, destination: link.href });
      }
    }, listenerOptions);
    document.addEventListener('click', (event) => {
      if (!root.contains(event.target) && event.target !== backdrop) closeSearch();
    }, listenerOptions);
    document.addEventListener('keydown', (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLocaleLowerCase() === 'k') {
        event.preventDefault();
        const wasFocused = document.activeElement === input;
        input.focus();
        input.select();
        if (wasFocused) openSearchForInput();
      }
    }, listenerOptions);

    root.__labPredictiveCleanup = () => {
      cancelPendingSearch();
      window.clearTimeout(backdropRemovalTimer);
      if (backdropFrame) window.cancelAnimationFrame(backdropFrame);
      listenerController.abort();
      root.classList.remove('is-command-open');
      results.hidden = true;
      results.removeAttribute('aria-busy');
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      backdrop?.remove();
      backdrop = undefined;
      if (!document.querySelector('[data-lab-predictive-search].is-command-open')) document.body.classList.remove('lab-search-command-open');
      delete root.dataset.labPredictiveReady;
      delete root.__labPredictiveCleanup;
    };
  };

  const predictiveSearchRootsIn = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-lab-predictive-search]')) roots.push(scope);
    scope.querySelectorAll?.('[data-lab-predictive-search]').forEach((root) => roots.push(root));
    return [...new Set(roots)];
  };

  const initPredictiveSearches = (scope = document) => {
    predictiveSearchRootsIn(scope).forEach(initPredictiveSearch);
  };

  initPredictiveSearches();

  document.querySelectorAll('[data-lab-product-gallery]').forEach((gallery) => {
    const track = gallery.querySelector('[data-lab-product-gallery-track]');
    const slides = [...gallery.querySelectorAll('[data-lab-product-slide]')];
    const previous = gallery.querySelector('[data-lab-product-gallery-prev]');
    const next = gallery.querySelector('[data-lab-product-gallery-next]');
    const currentLabel = gallery.querySelector('[data-lab-product-gallery-current]');
    if (!track || slides.length < 2) return;

    let currentIndex = 0;
    let scrollFrame;
    const updateCurrent = (index) => {
      currentIndex = Math.max(0, Math.min(slides.length - 1, index));
      if (currentLabel) currentLabel.textContent = String(currentIndex + 1);
      previous?.toggleAttribute('disabled', currentIndex === 0);
      next?.toggleAttribute('disabled', currentIndex === slides.length - 1);
    };
    const showSlide = (index) => {
      const targetIndex = Math.max(0, Math.min(slides.length - 1, index));
      track.scrollTo({ left: slides[targetIndex].offsetLeft, behavior: 'smooth' });
      updateCurrent(targetIndex);
    };

    previous?.addEventListener('click', () => showSlide(currentIndex - 1));
    next?.addEventListener('click', () => showSlide(currentIndex + 1));
    track.addEventListener('scroll', () => {
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(() => {
        const nearestIndex = slides.reduce((bestIndex, slide, index) => {
          return Math.abs(slide.offsetLeft - track.scrollLeft) < Math.abs(slides[bestIndex].offsetLeft - track.scrollLeft)
            ? index
            : bestIndex;
        }, 0);
        updateCurrent(nearestIndex);
      });
    }, { passive: true });
    updateCurrent(0);
  });

  const galleryImageButtons = [...document.querySelectorAll('[data-lab-gallery-item]')];
  if (galleryImageButtons.length) {
    const dialog = document.createElement('dialog');
    dialog.className = 'lab-gallery-lightbox';
    dialog.setAttribute('aria-label', 'Product image viewer');
    const image = document.createElement('img');
    const close = document.createElement('button');
    const previous = document.createElement('button');
    const next = document.createElement('button');
    const counter = document.createElement('span');
    close.type = 'button';
    previous.type = 'button';
    next.type = 'button';
    close.className = 'lab-gallery-lightbox__close';
    previous.className = 'lab-gallery-lightbox__arrow lab-gallery-lightbox__arrow--prev';
    next.className = 'lab-gallery-lightbox__arrow lab-gallery-lightbox__arrow--next';
    counter.className = 'lab-gallery-lightbox__counter';
    close.setAttribute('aria-label', 'Close image viewer');
    previous.setAttribute('aria-label', 'Previous image');
    next.setAttribute('aria-label', 'Next image');
    dialog.append(image, close, previous, next, counter);
    document.body.append(dialog);

    let activeImages = [];
    let activeImageIndex = 0;
    const showLightboxImage = (index) => {
      if (!activeImages.length) return;
      activeImageIndex = (index + activeImages.length) % activeImages.length;
      const button = activeImages[activeImageIndex];
      image.src = button.dataset.labGalleryFull || button.querySelector('img')?.currentSrc || '';
      image.alt = button.dataset.labGalleryAlt || button.querySelector('img')?.alt || '';
      counter.textContent = `${activeImageIndex + 1} / ${activeImages.length}`;
      previous.hidden = activeImages.length < 2;
      next.hidden = activeImages.length < 2;
    };

    galleryImageButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const productGallery = button.closest('[data-lab-product-gallery]');
        activeImages = [...(productGallery || document).querySelectorAll('[data-lab-gallery-item]')];
        showLightboxImage(activeImages.indexOf(button));
        dialog.showModal();
      });
    });
    close.addEventListener('click', () => dialog.close());
    previous.addEventListener('click', () => showLightboxImage(activeImageIndex - 1));
    next.addEventListener('click', () => showLightboxImage(activeImageIndex + 1));
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') showLightboxImage(activeImageIndex - 1);
      if (event.key === 'ArrowRight') showLightboxImage(activeImageIndex + 1);
    });
  }

  document.querySelectorAll('[data-lab-studio-search]').forEach((input) => {
    const directory = input.closest('.lab-studios-directory');
    const cards = [...directory.querySelectorAll('[data-lab-studio-card]')];
    const emptyState = directory.querySelector('[data-lab-studio-empty]');
    const clearButton = directory.querySelector('[data-lab-studio-search-clear]');
    const visibleCount = directory.querySelector('[data-lab-studio-visible-count]');
    let urlTimer;

    const filterStudios = (syncUrl = true) => {
      const query = input.value.trim().toLocaleLowerCase();
      let visible = 0;
      cards.forEach((card) => {
        const searchable = String(card.dataset.labStudioSearchText || card.dataset.labStudioName || '').toLocaleLowerCase();
        const matches = !query || searchable.includes(query);
        card.hidden = !matches;
        if (matches) visible += 1;
      });
      if (emptyState) emptyState.hidden = visible !== 0;
      if (visibleCount) visibleCount.textContent = String(visible);
      if (syncUrl) {
        window.clearTimeout(urlTimer);
        urlTimer = window.setTimeout(() => {
          const url = new URL(window.location.href);
          if (query) url.searchParams.set('q', input.value.trim());
          else url.searchParams.delete('q');
          window.history.replaceState({}, '', url);
        }, 180);
      }
    };

    input.addEventListener('input', filterStudios);
    clearButton?.addEventListener('click', () => {
      input.value = '';
      filterStudios();
      input.focus();
    });
    const initialQuery = new URL(window.location.href).searchParams.get('q');
    if (initialQuery) {
      input.value = initialQuery;
      filterStudios(false);
    } else {
      filterStudios(false);
    }
  });

  document.querySelectorAll('[data-lab-home-grid]').forEach((grid) => {
    const home = grid.closest('.lab-home');
    const filterPanel = home?.querySelector('[data-lab-home-filter-panel]');
    if (!home || !filterPanel) return;

    let cards = [...grid.querySelectorAll('[data-lab-resource-card]')];
    const originalOrder = new Map(cards.map((card, index) => [card, index]));
    const filterButtons = [...filterPanel.querySelectorAll('[data-lab-home-filter]')];
    const priceForm = filterPanel.querySelector('[data-lab-home-price-form]');
    const minimumInput = filterPanel.querySelector('[data-lab-home-price-min]');
    const maximumInput = filterPanel.querySelector('[data-lab-home-price-max]');
    const minimumRange = filterPanel.querySelector('[data-lab-home-price-min-range]');
    const maximumRange = filterPanel.querySelector('[data-lab-home-price-max-range]');
    const priceFill = filterPanel.querySelector('[data-lab-home-price-fill]');
    const resetButton = filterPanel.querySelector('[data-lab-home-filter-reset]');
    const countLabel = home.querySelector('[data-lab-home-visible-count]');
    const emptyState = home.querySelector('[data-lab-home-filter-empty]');
    const sortSelect = home.querySelector('[data-lab-home-sort]');
    const mobileSortSelect = home.querySelector('[data-lab-home-mobile-sort]');
    const mobileFilterCount = home.querySelector('[data-lab-mobile-filter-count]');
    const categoryLinks = [...home.querySelectorAll('[data-lab-home-category]')];
    const frameworkLinks = [...home.querySelectorAll('[data-lab-framework-card]')];
    const homeMain = home.querySelector('.lab-home-board__main');
    let pagination = home.querySelector('[data-lab-home-pagination]');

    const normalize = (value) => String(value || '')
      .toLowerCase()
      .replace(/&amp;/g, ' ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

    const aliases = new Map([
      ['mlos', 'mlo'],
      ['scripts', 'script'],
      ['maps', 'map'],
      ['vehicles', 'vehicle'],
      ['cars', 'car'],
      ['clothes', 'clothing'],
      ['bundles', 'bundle'],
      ['creator stores', 'creator']
    ]);

    const normalizedNeedle = (value) => {
      const normalized = normalize(value);
      return aliases.get(normalized) || normalized;
    };

    const cardMatchesValue = (card, group, value) => {
      const dataKey = `lab${group.charAt(0).toUpperCase()}${group.slice(1)}`;
      const needle = normalizedNeedle(value);
      if (group === 'collection') {
        return String(card.dataset[dataKey] || '')
          .split('|')
          .some((handle) => normalize(handle) === normalize(value));
      }
      const haystack = normalize(card.dataset[dataKey]);
      if (group === 'availability') return haystack === needle;
      return haystack.includes(needle);
    };

    const countCategoryCards = (value, group = 'type') => {
      const normalized = normalizedNeedle(value);
      if (normalized === 'all' || normalized === 'all resources') return cards.length;
      if (normalized === 'free') {
        return cards.filter((card) => Number(card.dataset.labPriceCents || 0) === 0).length;
      }
      return cards.filter((card) => cardMatchesValue(card, group, value)).length;
    };

    const updateDerivedCounts = () => {
      categoryLinks.forEach((link) => {
        const countElement = link.querySelector('[data-lab-category-count]');
        if (!countElement || countElement.dataset.serverAuthoritative === 'true') return;
        countElement.textContent = String(countCategoryCards(
          link.dataset.labCategoryValue,
          link.dataset.labCategoryGroup || 'type'
        ));
      });

      frameworkLinks.forEach((link) => {
        const countElement = link.querySelector('[data-lab-framework-count]');
        if (!countElement || Number(countElement.dataset.serverCount || 0) > 0) return;
        const count = cards.filter((card) => cardMatchesValue(card, 'framework', link.dataset.labFrameworkCard)).length;
        countElement.textContent = String(count);
      });
    };

    const updateFilterButtonCounts = () => {
      filterButtons.forEach((button) => {
        const countElement = button.querySelector('small');
        if (!countElement) return;
        const serverCount = Number(button.dataset.serverCount || 0);
        const loadedCount = cards.filter((card) => cardMatchesValue(card, button.dataset.filterGroup, button.dataset.filterValue)).length;
        const serverIsAuthoritative = button.dataset.serverAuthoritative === 'true';
        countElement.textContent = String(serverIsAuthoritative ? serverCount : loadedCount);
      });
    };

    const remoteCountCache = new Map();
    const readRemoteCount = async (href) => {
      if (!remoteCountCache.has(href)) {
        remoteCountCache.set(href, fetch(href, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then((response) => {
            if (!response.ok) throw new Error(`Category count request failed with ${response.status}`);
            return response.text();
          })
          .then((html) => {
            const fragment = new DOMParser().parseFromString(html, 'text/html');
            return Number(fragment.querySelector('[data-lab-collection-total]')?.textContent || 0);
          })
          .catch(() => 0));
      }
      return remoteCountCache.get(href);
    };

    const syncRemoteCount = async (link, countElement, group, value) => {
      const count = await readRemoteCount(link.href);
      if (!count || !countElement?.isConnected) return;

      countElement.textContent = String(count);
      countElement.dataset.serverCount = String(count);
      countElement.dataset.serverAuthoritative = 'true';

      const matchingFilter = filterButtons.find((button) => {
        return button.dataset.filterGroup === group
          && normalizedNeedle(button.dataset.filterValue) === normalizedNeedle(value);
      });
      if (matchingFilter) {
        matchingFilter.dataset.serverCount = String(count);
        matchingFilter.dataset.serverAuthoritative = 'true';
        const filterCount = matchingFilter.querySelector('small');
        if (filterCount) filterCount.textContent = String(count);
      }
    };

    const updateRemoteCounts = () => {
      categoryLinks.forEach((link) => {
        const countElement = link.querySelector('[data-lab-category-count]');
        if (!countElement || countElement.dataset.serverAuthoritative === 'true') return;
        syncRemoteCount(
          link,
          countElement,
          link.dataset.labCategoryGroup || 'type',
          link.dataset.labCategoryValue
        );
      });

      frameworkLinks.forEach((link) => {
        const countElement = link.querySelector('[data-lab-framework-count]');
        if (!countElement || Number(countElement.dataset.serverCount || 0) > 0) return;
        syncRemoteCount(link, countElement, 'framework', link.dataset.labFrameworkCard);
      });
    };

    const updatePriceFill = () => {
      if (!minimumRange || !maximumRange || !priceFill) return;
      const ceiling = Number(maximumRange.max) || 1;
      const start = Math.max(0, Math.min(100, (Number(minimumRange.value) / ceiling) * 100));
      const end = Math.max(start, Math.min(100, (Number(maximumRange.value) / ceiling) * 100));
      priceFill.style.left = `${start}%`;
      priceFill.style.right = `${100 - end}%`;
    };

    const syncRangeToFields = (changedRange) => {
      if (!minimumRange || !maximumRange) return;
      if (Number(minimumRange.value) > Number(maximumRange.value)) {
        if (changedRange === minimumRange) maximumRange.value = minimumRange.value;
        else minimumRange.value = maximumRange.value;
      }
      const ceiling = Number(maximumRange.max) || 0;
      if (minimumInput) minimumInput.value = Number(minimumRange.value) > 0 ? minimumRange.value : '';
      if (maximumInput) maximumInput.value = Number(maximumRange.value) < ceiling ? maximumRange.value : '';
      updatePriceFill();
      applyFilters();
    };

    const syncFieldsToRange = () => {
      if (!minimumRange || !maximumRange) return;
      const ceiling = Number(maximumRange.max) || 0;
      const minimum = !minimumInput || minimumInput.value === '' ? 0 : Number(minimumInput.value);
      const maximum = !maximumInput || maximumInput.value === '' ? ceiling : Number(maximumInput.value);
      minimumRange.value = String(Math.max(0, Math.min(ceiling, minimum)));
      maximumRange.value = String(Math.max(Number(minimumRange.value), Math.min(ceiling, maximum)));
      updatePriceFill();
    };

    const applyFilters = () => {
      const selectedByGroup = new Map();
      filterButtons.forEach((button) => {
        if (!button.classList.contains('is-active')) return;
        const group = button.dataset.filterGroup;
        const values = selectedByGroup.get(group) || [];
        values.push(normalizedNeedle(button.dataset.filterValue));
        selectedByGroup.set(group, values);
      });

      const minimum = !minimumInput || minimumInput.value === '' ? null : Number(minimumInput.value) * 100;
      const maximum = !maximumInput || maximumInput.value === '' ? null : Number(maximumInput.value) * 100;
      let visible = 0;

      cards.forEach((card) => {
        const matchesGroups = [...selectedByGroup.entries()].every(([group, values]) => {
          return values.some((value) => cardMatchesValue(card, group, value));
        });
        const price = Number(card.dataset.labPriceCents || 0);
        const matchesMinimum = minimum === null || Number.isNaN(minimum) || price >= minimum;
        const matchesMaximum = maximum === null || Number.isNaN(maximum) || price <= maximum;
        const matches = matchesGroups && matchesMinimum && matchesMaximum;

        card.hidden = !matches;
        if (matches) visible += 1;
      });

      const hasFilters = selectedByGroup.size > 0 || minimum !== null || maximum !== null;
      const activeFilterCount = filterButtons.filter((button) => button.classList.contains('is-active')).length
        + (minimum !== null || maximum !== null ? 1 : 0);
      if (mobileFilterCount) {
        mobileFilterCount.textContent = String(activeFilterCount);
        mobileFilterCount.toggleAttribute('hidden', activeFilterCount === 0);
      }
      if (countLabel) {
        countLabel.textContent = hasFilters
          ? `${visible} matching resources shown`
          : `Showing ${visible} on this page`;
      }
      if (emptyState) emptyState.hidden = visible !== 0;
    };

    const sortCards = () => {
      const mode = sortSelect?.value || 'manual';
      const sorted = [...cards].sort((left, right) => {
        if (mode === 'price-ascending') {
          return Number(left.dataset.labPriceCents || 0) - Number(right.dataset.labPriceCents || 0);
        }
        if (mode === 'price-descending') {
          return Number(right.dataset.labPriceCents || 0) - Number(left.dataset.labPriceCents || 0);
        }
        if (mode === 'created-descending') {
          return Number(right.dataset.labCreated || 0) - Number(left.dataset.labCreated || 0);
        }
        return originalOrder.get(left) - originalOrder.get(right);
      });
      sorted.forEach((card) => grid.append(card));
    };

    filterButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const active = button.classList.toggle('is-active');
        button.setAttribute('aria-pressed', String(active));
        applyFilters();
        publishLabEvent('home_filter', {
          group: button.dataset.filterGroup || '',
          value: button.dataset.filterValue || '',
          active
        });
      });
    });

    priceForm?.addEventListener('submit', (event) => {
      event.preventDefault();
      syncFieldsToRange();
      applyFilters();
      publishLabEvent('home_price_filter', {
        minimum: minimumInput?.value || '0',
        maximum: maximumInput?.value || maximumRange?.max || ''
      });
    });

    minimumRange?.addEventListener('input', () => syncRangeToFields(minimumRange));
    maximumRange?.addEventListener('input', () => syncRangeToFields(maximumRange));
    minimumRange?.addEventListener('change', () => publishLabEvent('home_price_filter', { minimum: minimumRange.value, maximum: maximumRange?.value || '' }));
    maximumRange?.addEventListener('change', () => publishLabEvent('home_price_filter', { minimum: minimumRange?.value || '', maximum: maximumRange.value }));
    minimumInput?.addEventListener('input', syncFieldsToRange);
    maximumInput?.addEventListener('input', syncFieldsToRange);

    resetButton?.addEventListener('click', () => {
      filterButtons.forEach((button) => {
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
      });
      if (minimumInput) minimumInput.value = '';
      if (maximumInput) maximumInput.value = '';
      if (minimumRange) minimumRange.value = minimumRange.min;
      if (maximumRange) maximumRange.value = maximumRange.max;
      updatePriceFill();
      if (sortSelect) sortSelect.value = 'manual';
      sortCards();
      applyFilters();
    });

    sortSelect?.addEventListener('change', () => {
      if (mobileSortSelect) mobileSortSelect.value = sortSelect.value;
      sortCards();
      publishLabEvent('home_sort', { sort: sortSelect.value });
    });
    mobileSortSelect?.addEventListener('change', () => {
      if (sortSelect) sortSelect.value = mobileSortSelect.value;
      sortCards();
      publishLabEvent('home_sort', { sort: mobileSortSelect.value });
    });
    const loadCatalogPage = async (href, historyMode = 'push', scrollToResults = true) => {
      if (grid.getAttribute('aria-busy') === 'true') return false;

      const targetUrl = preserveThemePreviewParams(new URL(href, window.location.href));
      const previousHeight = grid.offsetHeight;
      grid.setAttribute('aria-busy', 'true');
      grid.classList.add('is-loading');
      grid.style.minHeight = `${previousHeight}px`;

      try {
        const response = await fetch(targetUrl.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error(`Catalog request failed with ${response.status}`);

        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextGrid = documentFragment.querySelector('[data-lab-home-grid]');
        if (!nextGrid) throw new Error('Catalog grid is missing from the next page');

        const nextCards = [...nextGrid.querySelectorAll('[data-lab-resource-card]')];
        grid.replaceChildren(...nextCards);
        cards = nextCards;
        originalOrder.clear();
        cards.forEach((card, index) => originalOrder.set(card, index));
        updateSavedButtons(grid);
        initAdCampaigns(grid);
        updateDerivedCounts();
        updateFilterButtonCounts();
        sortCards();
        applyFilters();

        const nextPagination = documentFragment.querySelector('[data-lab-home-pagination]');
        if (pagination && nextPagination) {
          pagination.replaceWith(nextPagination);
          pagination = nextPagination;
        } else if (pagination) {
          pagination.remove();
          pagination = null;
        } else if (nextPagination) {
          grid.insertAdjacentElement('afterend', nextPagination);
          pagination = nextPagination;
        }

        grid.classList.add('is-entering');
        window.setTimeout(() => grid.classList.remove('is-entering'), 360);
        if (historyMode === 'push') window.history.pushState({}, '', targetUrl.toString());
        if (historyMode === 'replace') window.history.replaceState({}, '', targetUrl.toString());
        if (scrollToResults && homeMain) {
          homeMain.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        publishLabEvent('catalog_page', {
          page: targetUrl.searchParams.get('page') || '1',
          visible: cards.length
        });
        return true;
      } catch (error) {
        console.error(error);
        if (historyMode !== 'none') {
          window.location.assign(targetUrl.toString());
        } else {
          grid.classList.remove('is-loading');
        }
        return false;
      } finally {
        grid.removeAttribute('aria-busy');
        grid.classList.remove('is-loading');
        grid.style.minHeight = '';
      }
    };

    home.addEventListener('click', (event) => {
      const link = event.target.closest('[data-lab-home-pagination] a');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      loadCatalogPage(link.href);
    });
    window.addEventListener('popstate', () => {
      if (home.isConnected && !document.querySelector('[data-lab-collection-page]')) {
        loadCatalogPage(window.location.href, 'none', false);
      }
    });
    updateDerivedCounts();
    updateFilterButtonCounts();
    updateRemoteCounts();
    updatePriceFill();
    applyFilters();
  });

  const readStoredList = (key) => {
    try {
      const value = JSON.parse(window.localStorage.getItem(key) || '[]');
      return Array.isArray(value) ? value : [];
    } catch (error) {
      console.warn(`Could not read ${key}`, error);
      return [];
    }
  };

  const readStoredObject = (key, fallback = {}) => {
    try {
      const value = JSON.parse(window.localStorage.getItem(key) || 'null');
      return value && typeof value === 'object' && !Array.isArray(value) ? value : fallback;
    } catch (error) {
      console.warn(`Could not read ${key}`, error);
      return fallback;
    }
  };

  const VIEW_HISTORY_KEY = 'labViewedResourcesV1';
  const SAVED_RESOURCES_KEY = 'labSavedResources';
  const INTERESTS_KEY = 'labInterestsV1';
  const FOLLOWED_STUDIOS_KEY = 'labFollowedStudiosV1';
  const viewedProductData = document.querySelector('[data-lab-viewed-product]');
  if (viewedProductData) {
    try {
      const product = JSON.parse(viewedProductData.textContent || '{}');
      if (product.handle) {
        const history = readStoredList(VIEW_HISTORY_KEY).filter((item) => item?.handle !== product.handle);
        history.unshift({
          handle: product.handle,
          title: product.title || '',
          vendor: product.vendor || '',
          type: product.type || '',
          tags: Array.isArray(product.tags) ? product.tags.slice(0, 30) : [],
          url: product.url || `/products/${encodeURIComponent(product.handle)}`,
          image: product.image || '',
          priceCents: Number(product.priceCents || 0),
          priceLabel: product.priceLabel || '',
          creatorUrl: product.creatorUrl || '',
          viewedAt: Date.now()
        });
        window.localStorage.setItem(VIEW_HISTORY_KEY, JSON.stringify(history.slice(0, 20)));
        publishLabEvent('resource_view', { handle: product.handle, type: product.type || '', vendor: product.vendor || '' });
      }
    } catch (error) {
      console.warn('Could not update viewed resource history', error);
    }
  }

  const saved = new Set(readStoredList(SAVED_RESOURCES_KEY));
  const followedStudios = new Map(readStoredList(FOLLOWED_STUDIOS_KEY)
    .filter((studio) => studio?.name)
    .map((studio) => [String(studio.name).trim().toLowerCase(), studio]));
  const updateSavedButtons = (scope = document) => {
    scope.querySelectorAll('[data-lab-save]').forEach((button) => {
      const isSaved = saved.has(button.getAttribute('data-lab-save'));
      button.classList.toggle('is-saved', isSaved);
      button.setAttribute('aria-pressed', String(isSaved));
      const label = button.querySelector('[data-lab-save-label]');
      if (label) label.textContent = isSaved ? 'Saved' : 'Save resource';
    });
    document.querySelectorAll('[data-lab-header-saved-count]').forEach((counter) => {
      counter.textContent = String(saved.size);
      counter.toggleAttribute('hidden', saved.size === 0);
    });
  };
  const updateFollowButtons = (scope = document) => {
    scope.querySelectorAll('[data-lab-follow-studio]').forEach((button) => {
      const key = String(button.dataset.labFollowStudio || '').trim().toLowerCase();
      const isFollowed = followedStudios.has(key);
      button.classList.toggle('is-followed', isFollowed);
      button.setAttribute('aria-pressed', String(isFollowed));
      const label = button.querySelector('[data-lab-follow-label]');
      if (label) label.textContent = isFollowed ? 'Following studio' : 'Follow studio';
    });
  };
  updateSavedButtons();
  updateFollowButtons();

  const getProductTags = (product) => {
    const tags = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',');
    return tags.map((tag) => String(tag).trim()).filter(Boolean);
  };

  const getMiniResourcePresentation = (panel) => {
    const locale = panel.dataset.labSideFeedLocale || document.documentElement.lang || 'en';
    return {
      compactDateFormatter: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }),
      fullDateFormatter: new Intl.DateTimeFormat(locale, { dateStyle: 'long' }),
      moneyFormatter: new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: panel.dataset.labSideFeedCurrency || 'USD'
      }),
      root: String(panel.dataset.labSideFeedRoot || '/').replace(/\/?$/, '/'),
      freeLabel: panel.dataset.labSideFeedFreeLabel || 'Free'
    };
  };

  const createMiniResource = (product, presentation, showDate = false) => {
    const link = document.createElement('a');
    link.className = 'lab-mini-resource';
    link.href = `${presentation.root}products/${encodeURIComponent(product.handle)}`;

    const imageData = Array.isArray(product.images) ? product.images[0] : product.image;
    const imageSource = typeof imageData === 'string' ? imageData : imageData?.src;
    if (imageSource) {
      const image = document.createElement('img');
      image.src = imageSource;
      image.alt = typeof imageData === 'object' ? imageData.alt || product.title || '' : product.title || '';
      image.loading = 'lazy';
      link.append(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'lab-mini-resource__placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      link.append(placeholder);
    }

    const copy = document.createElement('span');
    copy.className = 'lab-mini-resource__copy';
    const title = document.createElement('strong');
    const meta = document.createElement('small');
    const vendor = document.createElement('span');
    title.textContent = product.title || 'Untitled resource';
    vendor.textContent = product.vendor || '';
    meta.className = 'lab-mini-resource__meta';
    meta.append(vendor);

    const publishedAt = product.published_at || product.created_at;
    const publishedDate = publishedAt ? new Date(publishedAt) : null;
    if (showDate && publishedDate && !Number.isNaN(publishedDate.getTime())) {
      const time = document.createElement('time');
      time.dateTime = publishedDate.toISOString();
      time.textContent = presentation.compactDateFormatter.format(publishedDate);
      time.title = `Published ${presentation.fullDateFormatter.format(publishedDate)}`;
      meta.append(time);
    }

    copy.append(title, meta);
    link.append(copy);

    const prices = (Array.isArray(product.variants) ? product.variants : [])
      .map((variant) => Number(variant.price))
      .filter(Number.isFinite);
    const price = prices.length ? Math.min(...prices) : 0;
    const money = document.createElement('b');
    money.textContent = price === 0 ? presentation.freeLabel : presentation.moneyFormatter.format(price);
    link.append(money);
    return link;
  };

  const renderMiniResources = (target, products, panel, showDate = false) => {
    const presentation = getMiniResourcePresentation(panel);
    target.replaceChildren(...products.map((product) => createMiniResource(product, presentation, showDate)));
  };

  const getInterests = () => {
    const stored = readStoredObject(INTERESTS_KEY, {});
    return {
      frameworks: Array.isArray(stored.frameworks) ? stored.frameworks : [],
      types: Array.isArray(stored.types) ? stored.types : [],
      price: ['any', 'free', 'paid'].includes(stored.price) ? stored.price : 'any'
    };
  };

  const countInterests = (interests = getInterests()) => {
    return interests.frameworks.length + interests.types.length + (interests.price === 'any' ? 0 : 1);
  };

  const populateInterestForms = () => {
    const interests = getInterests();
    document.querySelectorAll('[data-lab-interest-form]').forEach((form) => {
      form.querySelectorAll('[data-lab-interest-option="frameworks"]').forEach((input) => {
        input.checked = interests.frameworks.includes(input.value);
      });
      form.querySelectorAll('[data-lab-interest-option="types"]').forEach((input) => {
        input.checked = interests.types.includes(input.value);
      });
      form.querySelectorAll('[data-lab-interest-option="price"]').forEach((input) => {
        input.checked = input.value === interests.price;
      });
    });

    const count = countInterests(interests);
    document.querySelectorAll('[data-lab-interest-summary]').forEach((summary) => {
      if (!count) return;
      const selections = [...interests.frameworks, ...interests.types];
      if (interests.price !== 'any') selections.push(interests.price);
      summary.textContent = `${count} preferences saved: ${selections.join(', ')}.`;
    });
    document.querySelectorAll('[data-lab-my-stat="interests"]').forEach((target) => {
      target.textContent = String(count);
    });
  };

  const resetRecommendationPanels = () => {
    document.querySelectorAll('[data-lab-recommendations]').forEach((panel) => {
      delete panel.dataset.labRecommendationLoaded;
      if (!panel.hidden) refreshRecommendations(panel);
    });
  };

  document.querySelectorAll('[data-lab-interest-toggle]').forEach((button) => {
    const bar = button.closest('[data-lab-interest-bar]');
    const panel = bar?.querySelector('[data-lab-interest-panel]');
    button.addEventListener('click', () => {
      const open = panel?.hasAttribute('hidden');
      panel?.toggleAttribute('hidden', !open);
      button.setAttribute('aria-expanded', String(open));
      if (open) panel.querySelector('input')?.focus();
    });
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-lab-interest-form]');
    if (!form) return;
    event.preventDefault();
    const interests = {
      frameworks: [...form.querySelectorAll('[data-lab-interest-option="frameworks"]:checked')].map((input) => input.value),
      types: [...form.querySelectorAll('[data-lab-interest-option="types"]:checked')].map((input) => input.value),
      price: form.querySelector('[data-lab-interest-option="price"]:checked')?.value || 'any',
      updatedAt: Date.now()
    };
    window.localStorage.setItem(INTERESTS_KEY, JSON.stringify(interests));
    populateInterestForms();
    resetRecommendationPanels();
    refreshMyLab();
    const status = form.querySelector('[data-lab-interest-status]');
    if (status) status.textContent = 'Preferences saved.';
    publishLabEvent('interests_saved', {
      frameworks: interests.frameworks.join(','),
      types: interests.types.join(','),
      price: interests.price
    });
  });

  document.addEventListener('click', (event) => {
    const reset = event.target.closest('[data-lab-interest-reset]');
    if (!reset) return;
    window.localStorage.removeItem(INTERESTS_KEY);
    populateInterestForms();
    resetRecommendationPanels();
    refreshMyLab();
    const status = reset.closest('form')?.querySelector('[data-lab-interest-status]');
    if (status) status.textContent = 'Preferences reset.';
    publishLabEvent('interests_reset');
  });

  const createMyResource = (product, presentation, context) => {
    const handle = product.handle || '';
    const url = product.url || `${presentation.root}products/${encodeURIComponent(handle)}`;
    const article = document.createElement('article');
    article.className = 'lab-my-resource';

    const mediaLink = document.createElement('a');
    mediaLink.className = 'lab-my-resource__media';
    mediaLink.href = url;
    const imageData = Array.isArray(product.images) ? product.images[0] : product.image;
    const imageSource = typeof imageData === 'string' ? imageData : imageData?.src;
    if (imageSource) {
      const image = document.createElement('img');
      image.src = imageSource;
      image.alt = typeof imageData === 'object' ? imageData.alt || product.title || '' : product.title || '';
      image.loading = 'lazy';
      mediaLink.append(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'lab-my-resource__placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      mediaLink.append(placeholder);
    }

    const body = document.createElement('div');
    body.className = 'lab-my-resource__body';
    const title = document.createElement('a');
    title.className = 'lab-my-resource__title';
    title.href = url;
    title.textContent = product.title || 'Untitled resource';
    const vendor = document.createElement('span');
    vendor.className = 'lab-my-resource__vendor';
    vendor.textContent = product.vendor || '';

    const footer = document.createElement('div');
    footer.className = 'lab-my-resource__footer';
    const prices = (Array.isArray(product.variants) ? product.variants : [])
      .map((variant) => Number(variant.price))
      .filter(Number.isFinite);
    const price = prices.length ? Math.min(...prices) : Number(product.priceCents || 0) / 100;
    const priceLabel = document.createElement('strong');
    priceLabel.textContent = product.priceLabel || (price === 0 ? presentation.freeLabel : presentation.moneyFormatter.format(price));
    const saveButton = document.createElement('button');
    saveButton.className = 'lab-my-resource__save';
    saveButton.type = 'button';
    saveButton.dataset.labSave = handle;
    saveButton.innerHTML = '<span data-lab-save-label>Save resource</span>';
    footer.append(priceLabel, saveButton);
    body.append(title, vendor, footer);
    article.append(mediaLink, body);
    article.dataset.labRecommendationContext = context;
    return article;
  };

  const myLabRoot = document.querySelector('[data-lab-my-lab]');
  let myLabProducts = new Map();
  let refreshMyLab = () => {};
  if (myLabRoot) {
    const presentation = {
      root: String(myLabRoot.dataset.labMyLabRoot || '/').replace(/\/?$/, '/'),
      freeLabel: myLabRoot.dataset.labMyLabFreeLabel || 'Free',
      moneyFormatter: new Intl.NumberFormat(myLabRoot.dataset.labMyLabLocale || document.documentElement.lang || 'en', {
        style: 'currency',
        currency: myLabRoot.dataset.labMyLabCurrency || 'USD'
      })
    };
    const browseUrl = `${presentation.root}collections/all`;
    const renderEmpty = (target, message) => {
      const empty = document.createElement('div');
      empty.className = 'lab-my-empty';
      const text = document.createElement('p');
      text.textContent = message;
      const link = document.createElement('a');
      link.className = 'lab-button lab-button--secondary';
      link.href = browseUrl;
      link.textContent = 'Browse catalog';
      empty.append(text, link);
      target.replaceChildren(empty);
    };

    refreshMyLab = () => {
      const history = readStoredList(VIEW_HISTORY_KEY);
      const savedProducts = [...saved].map((handle) => myLabProducts.get(handle)).filter(Boolean);
      const recentProducts = history.map((item) => myLabProducts.get(item?.handle) || item).filter((item) => item?.handle);
      const savedTarget = myLabRoot.querySelector('[data-lab-my-saved]');
      const recentTarget = myLabRoot.querySelector('[data-lab-my-recent]');

      if (savedTarget) {
        if (savedProducts.length) savedTarget.replaceChildren(...savedProducts.map((product) => createMyResource(product, presentation, 'saved')));
        else renderEmpty(savedTarget, 'Save resources from the catalog to keep them here.');
      }
      if (recentTarget) {
        if (recentProducts.length) recentTarget.replaceChildren(...recentProducts.map((product) => createMyResource(product, presentation, 'recent')));
        else renderEmpty(recentTarget, 'Resources you open will appear here.');
      }
      updateSavedButtons(myLabRoot);

      const studiosTarget = myLabRoot.querySelector('[data-lab-followed-studios]');
      const studios = [...followedStudios.values()];
      if (studiosTarget) {
        if (studios.length) {
          studiosTarget.replaceChildren(...studios.map((studio) => {
            const item = document.createElement('div');
            item.className = 'lab-followed-studio';
            const link = document.createElement('a');
            link.href = studio.url || browseUrl;
            link.textContent = studio.name;
            const button = document.createElement('button');
            button.type = 'button';
            button.dataset.labFollowStudio = studio.name;
            button.dataset.labFollowStudioUrl = studio.url || '';
            button.innerHTML = '<span data-lab-follow-label>Following studio</span>';
            item.append(link, button);
            return item;
          }));
          updateFollowButtons(studiosTarget);
        } else {
          studiosTarget.innerHTML = '<p class="lab-my-empty">Follow a studio from any resource page to keep it here.</p>';
        }
      }

      const stats = {
        saved: saved.size,
        recent: history.length,
        interests: countInterests(),
        studios: studios.length
      };
      Object.entries(stats).forEach(([name, value]) => {
        myLabRoot.querySelectorAll(`[data-lab-my-stat="${name}"]`).forEach((target) => { target.textContent = String(value); });
      });
    };

    const source = myLabRoot.dataset.labMyLabFeedUrl;
    if (source) {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('_lab_my', String(Date.now()));
      fetch(url.toString(), { cache: 'no-store', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then((response) => {
          if (!response.ok) throw new Error(`My Lab catalog request failed with ${response.status}`);
          return response.json();
        })
        .then((payload) => {
          myLabProducts = new Map((payload.products || []).map((product) => [product.handle, product]));
          refreshMyLab();
        })
        .catch((error) => {
          console.error(error);
          refreshMyLab();
        });
    } else {
      refreshMyLab();
    }

    myLabRoot.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-lab-my-tab]');
      if (tab) {
        const name = tab.dataset.labMyTab;
        myLabRoot.querySelectorAll('[data-lab-my-tab]').forEach((item) => {
          const active = item === tab;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', String(active));
          item.tabIndex = active ? 0 : -1;
        });
        myLabRoot.querySelectorAll('[data-lab-my-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.labMyPanel !== name;
        });
        publishLabEvent('my_lab_tab', { tab: name });
      }

      if (event.target.closest('[data-lab-my-clear-history]')) {
        window.localStorage.removeItem(VIEW_HISTORY_KEY);
        refreshMyLab();
        publishLabEvent('history_cleared');
      }
    });
  }

  populateInterestForms();

  document.querySelectorAll('[data-lab-product-recommendations][data-lab-recommendation-url]').forEach(async (section) => {
    try {
      const url = preserveThemePreviewParams(new URL(section.dataset.labRecommendationUrl, window.location.origin));
      const response = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error(`Product recommendations request failed with ${response.status}`);
      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const replacement = documentFragment.querySelector('[data-lab-product-recommendations]');
      if (!replacement) throw new Error('Product recommendations markup is missing');
      section.replaceWith(replacement);
      updateSavedButtons(replacement);
      publishLabEvent('recommendations_loaded', {
        count: replacement.querySelectorAll('[data-lab-resource-card]').length,
        source: 'shopify_related'
      });
    } catch (error) {
      console.error(error);
      const status = section.querySelector('.lab-product-recommendations__loading p');
      if (status) status.textContent = 'Related resources could not be loaded.';
    }
  });

  document.addEventListener('click', (event) => {
    const recommendationLink = event.target.closest('[data-lab-native-recommendation-grid] a[href*="/products/"]');
    if (recommendationLink) publishLabEvent('recommendation_click', { source: 'shopify_related', destination: recommendationLink.href });

    const personalizedLink = event.target.closest('[data-lab-recommendations] .lab-mini-resource');
    if (personalizedLink) publishLabEvent('recommendation_click', { source: 'browser_interests', destination: personalizedLink.href });

    const catalogLink = event.target.closest('[data-lab-resource-card] a[href*="/products/"]');
    if (catalogLink && !recommendationLink) publishLabEvent('catalog_resource_open', { destination: catalogLink.href });
  });

  const refreshSideFeed = async (panel, force = false) => {
    const target = panel.querySelector('[data-lab-side-feed-target]');
    const source = panel.dataset.labSideFeedUrl;
    const lastRefresh = Number(panel.dataset.labSideFeedRefreshedAt || 0);
    if (!target || !source || panel.dataset.labSideFeedLoading === 'true') return;
    if (!force && Date.now() - lastRefresh < 60000) return;

    panel.dataset.labSideFeedLoading = 'true';
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('_lab_feed', String(Date.now()));
      const response = await fetch(url.toString(), {
        cache: 'no-store',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`New resources request failed with ${response.status}`);

      const payload = await response.json();
      const requiredTag = String(panel.dataset.labSideFeedTag || '').trim().toLowerCase();
      const limit = Math.max(1, Number(panel.dataset.labSideFeedLimit) || 12);
      const products = (Array.isArray(payload.products) ? payload.products : [])
        .filter((product) => {
          if (!requiredTag) return true;
          return getProductTags(product).some((tag) => tag.toLowerCase() === requiredTag);
        })
        .sort((left, right) => {
          const leftDate = Date.parse(left.published_at || left.created_at || 0) || 0;
          const rightDate = Date.parse(right.published_at || right.created_at || 0) || 0;
          return rightDate - leftDate;
        })
        .slice(0, limit);

      if (products.length) {
        renderMiniResources(target, products, panel, true);
      }
      panel.dataset.labSideFeedRefreshedAt = String(Date.now());
    } catch (error) {
      console.error(error);
    } finally {
      panel.dataset.labSideFeedLoading = 'false';
    }
  };

  const refreshRecommendations = async (panel) => {
    const target = panel.querySelector('[data-lab-recommendation-target]');
    const source = panel.dataset.labRecommendationFeedUrl;
    if (!target || !source || panel.dataset.labRecommendationLoading === 'true' || panel.dataset.labRecommendationLoaded === 'true') return;

    panel.dataset.labRecommendationLoading = 'true';
    target.innerHTML = '<p class="lab-side-empty">Preparing recommendations...</p>';
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('_lab_recommendations', String(Date.now()));
      const response = await fetch(url.toString(), {
        cache: 'no-store',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`Recommendation request failed with ${response.status}`);

      const payload = await response.json();
      const products = Array.isArray(payload.products) ? payload.products : [];
      const history = readStoredList(VIEW_HISTORY_KEY);
      const productsByHandle = new Map(products.map((product) => [product.handle, product]));
      const recentProducts = history
        .map((item) => productsByHandle.get(item?.handle))
        .filter(Boolean)
        .slice(0, 4);
      const recentBlock = panel.querySelector('[data-lab-recently-viewed]');
      const recentTarget = panel.querySelector('[data-lab-recently-viewed-target]');
      if (recentBlock && recentTarget && recentProducts.length) {
        renderMiniResources(recentTarget, recentProducts, panel);
        recentBlock.hidden = false;
      }
      const tagWeights = new Map();
      const typeWeights = new Map();
      const vendorWeights = new Map();
      const normalizeInterest = (value) => {
        const normalized = String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
        return new Map([
          ['mlos', 'mlo'],
          ['scripts', 'script'],
          ['maps', 'map'],
          ['vehicles', 'vehicle'],
          ['clothes', 'clothing']
        ]).get(normalized) || normalized;
      };
      const addWeight = (map, value, weight) => {
        const key = normalizeInterest(value);
        if (key) map.set(key, (map.get(key) || 0) + weight);
      };

      history.forEach((item, index) => {
        const recencyWeight = Math.max(1, history.length - index);
        addWeight(typeWeights, item.type, recencyWeight * 6);
        addWeight(vendorWeights, item.vendor, recencyWeight * 3);
        (Array.isArray(item.tags) ? item.tags : []).forEach((tag) => addWeight(tagWeights, tag, recencyWeight * 2));
      });

      saved.forEach((handle) => {
        const product = productsByHandle.get(handle);
        if (!product) return;
        addWeight(typeWeights, product.product_type, 18);
        addWeight(vendorWeights, product.vendor, 10);
        getProductTags(product).forEach((tag) => addWeight(tagWeights, tag, 7));
      });

      const interests = getInterests();
      interests.types.forEach((type) => addWeight(typeWeights, type, 32));
      interests.frameworks.forEach((framework) => addWeight(tagWeights, framework, 36));
      followedStudios.forEach((studio) => addWeight(vendorWeights, studio.name, 42));

      const viewedHandles = new Set(history.map((item) => item?.handle).filter(Boolean));
      const scored = products.map((product) => {
        const typeScore = typeWeights.get(normalizeInterest(product.product_type)) || 0;
        const vendorScore = vendorWeights.get(normalizeInterest(product.vendor)) || 0;
        const tagScore = getProductTags(product).reduce((score, tag) => score + (tagWeights.get(normalizeInterest(tag)) || 0), 0);
        const prices = (Array.isArray(product.variants) ? product.variants : []).map((variant) => Number(variant.price)).filter(Number.isFinite);
        const minimumPrice = prices.length ? Math.min(...prices) : 0;
        const priceScore = interests.price === 'free' && minimumPrice === 0
          ? 18
          : interests.price === 'paid' && minimumPrice > 0
            ? 18
            : 0;
        return {
          product,
          score: typeScore + vendorScore + tagScore + priceScore,
          publishedAt: Date.parse(product.published_at || product.created_at || 0) || 0
        };
      }).sort((left, right) => right.score - left.score || right.publishedAt - left.publishedAt);

      const limit = Math.max(1, Number(panel.dataset.labRecommendationLimit) || 12);
      const unseen = scored.filter((item) => !viewedHandles.has(item.product.handle));
      const pool = unseen.length >= Math.min(limit, scored.length) ? unseen : scored;
      const recommendations = pool.slice(0, limit).map((item) => item.product);

      if (recommendations.length) {
        renderMiniResources(target, recommendations, panel);
        publishLabEvent('recommendations_loaded', { count: recommendations.length, source: 'browser_interests' });
      } else {
        target.innerHTML = '<p class="lab-side-empty">No recommendation candidates are available yet.</p>';
      }
      panel.dataset.labRecommendationLoaded = 'true';
    } catch (error) {
      console.error(error);
      target.innerHTML = '<p class="lab-side-empty">Recommendations could not be loaded. Please try again.</p>';
    } finally {
      panel.dataset.labRecommendationLoading = 'false';
    }
  };

  const initSideFeeds = (scope = document) => {
    scope.querySelectorAll('[data-lab-side-feed-url]').forEach((panel) => {
      refreshSideFeed(panel, true);
    });
  };

  initSideFeeds();
  document.querySelectorAll('[data-lab-for-you][data-lab-recommendations]').forEach((panel) => {
    refreshRecommendations(panel);
  });

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) initSideFeeds();
  });

  document.addEventListener('click', (event) => {
    const clearHistory = event.target.closest('[data-lab-clear-view-history]');
    if (clearHistory) {
      window.localStorage.removeItem(VIEW_HISTORY_KEY);
      clearHistory.closest('[data-lab-recently-viewed]')?.setAttribute('hidden', '');
      refreshMyLab();
      publishLabEvent('history_cleared');
      return;
    }

    const button = event.target.closest('[data-lab-save]');
    if (button) {
      event.preventDefault();
      event.stopPropagation();
      const handle = button.getAttribute('data-lab-save');
      const willSave = !saved.has(handle);

      if (willSave) saved.add(handle);
      else saved.delete(handle);

      updateSavedButtons();
      window.localStorage.setItem(SAVED_RESOURCES_KEY, JSON.stringify([...saved]));
      refreshMyLab();
      publishLabEvent(willSave ? 'resource_saved' : 'resource_unsaved', { handle });
      return;
    }

    const followButton = event.target.closest('[data-lab-follow-studio]');
    if (!followButton) return;
    event.preventDefault();
    event.stopPropagation();
    const name = String(followButton.dataset.labFollowStudio || '').trim();
    const key = name.toLowerCase();
    const willFollow = !followedStudios.has(key);
    if (willFollow) {
      followedStudios.set(key, {
        name,
        url: followButton.dataset.labFollowStudioUrl || '',
        followedAt: Date.now()
      });
    } else {
      followedStudios.delete(key);
    }
    window.localStorage.setItem(FOLLOWED_STUDIOS_KEY, JSON.stringify([...followedStudios.values()]));
    updateFollowButtons();
    refreshMyLab();
    resetRecommendationPanels();
    publishLabEvent(willFollow ? 'studio_followed' : 'studio_unfollowed', { studio: name });
  });

  const activateSideTab = (tab, moveFocus = false) => {
    const tabList = tab.closest('[data-lab-side-tabs]');
    const widget = tabList?.closest('.lab-side-list--tabs');
    if (!tabList || !widget) return;

    const tabName = tab.dataset.labSideTab;
    const tabs = [...tabList.querySelectorAll('[data-lab-side-tab]')];
    const panels = [...widget.querySelectorAll('[data-lab-side-panel]')];

    tabs.forEach((item) => {
      const selected = item === tab;
      item.classList.toggle('is-active', selected);
      item.setAttribute('aria-selected', String(selected));
      item.tabIndex = selected ? 0 : -1;
    });
    panels.forEach((panel) => {
      panel.hidden = panel.dataset.labSidePanel !== tabName;
    });
    const activePanel = panels.find((panel) => panel.dataset.labSidePanel === tabName);
    if (activePanel?.dataset.labSideFeedUrl) refreshSideFeed(activePanel);
    if (activePanel?.hasAttribute('data-lab-recommendations')) refreshRecommendations(activePanel);

    if (moveFocus) tab.focus();
  };

  document.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-lab-side-tab]');
    if (tab) activateSideTab(tab);
  });

  document.addEventListener('keydown', (event) => {
    const tab = event.target.closest('[data-lab-side-tab]');
    if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

    const tabs = [...tab.closest('[data-lab-side-tabs]').querySelectorAll('[data-lab-side-tab]')];
    const currentIndex = tabs.indexOf(tab);
    let nextIndex = currentIndex;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabs.length - 1;

    event.preventDefault();
    activateSideTab(tabs[nextIndex], true);
  });

  const initSlider = (slider) => {
    if (slider.dataset.labSliderReady === 'true') return;

    const allSlides = [...slider.querySelectorAll('[data-lab-slide]')];
    const slides = allSlides.filter((slide) => !slide.hidden);
    const allDots = [...slider.querySelectorAll('[data-lab-slide-dot]')];
    const dots = allDots.filter((dot) => {
      const visible = slides.some((slide) => slide.dataset.labSlideIndex === dot.dataset.labSlideDot);
      dot.hidden = !visible;
      return visible;
    });
    const previous = slider.querySelector('[data-lab-slider-prev]');
    const next = slider.querySelector('[data-lab-slider-next]');
    const navigation = slider.querySelector('.lab-feature-slider__navigation');

    slider.dataset.labSliderReady = 'true';
    if (slides.length < 2) {
      if (navigation) navigation.hidden = true;
      slides.forEach((slide, index) => {
        slide.classList.toggle('is-active', index === 0);
        slide.setAttribute('aria-hidden', String(index !== 0));
      });
      return;
    }

    if (navigation) navigation.hidden = false;
    const autoplay = slider.dataset.labSliderEnabled !== 'false';
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const interval = Math.max(4000, Number(slider.dataset.labSliderAutoplay) || 6000);
    let active = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let timer;
    let transitionTimer;
    let pointerStart = null;

    const show = (index, immediate = false) => {
      const nextIndex = (index + slides.length) % slides.length;
      const previousIndex = active;
      const forwardDistance = (nextIndex - previousIndex + slides.length) % slides.length;
      const leavingClass = forwardDistance > slides.length / 2
        ? 'is-leaving-right'
        : 'is-leaving-left';

      if (!immediate && nextIndex === previousIndex) return;
      if (transitionTimer) window.clearTimeout(transitionTimer);

      slides.forEach((slide, slideIndex) => {
        const selected = slideIndex === nextIndex;
        slide.classList.remove('is-leaving-left', 'is-leaving-right');
        if (!immediate && slideIndex === previousIndex) slide.classList.add(leavingClass);
        slide.classList.toggle('is-active', selected);
        slide.hidden = false;
        slide.setAttribute('aria-hidden', String(!selected));
      });

      active = nextIndex;
      dots.forEach((dot, dotIndex) => {
        const selected = dotIndex === active;
        dot.classList.toggle('is-active', selected);
        if (selected) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      if (!immediate) {
        transitionTimer = window.setTimeout(() => {
          slides.forEach((slide) => slide.classList.remove('is-leaving-left', 'is-leaving-right'));
        }, 850);
      }
    };

    const stop = () => {
      if (timer) window.clearInterval(timer);
      timer = undefined;
    };

    const start = () => {
      stop();
      if (!autoplay || reduceMotion || document.hidden) return;
      timer = window.setInterval(() => show(active + 1), interval);
    };

    const move = (index) => {
      show(index);
      start();
    };

    slider.__labSliderShow = (requestedIndex) => {
      const originalIndex = slides.findIndex((slide) => Number(slide.dataset.labSlideIndex) === Number(requestedIndex));
      move(originalIndex >= 0 ? originalIndex : requestedIndex);
    };
    previous?.addEventListener('click', () => move(active - 1));
    next?.addEventListener('click', () => move(active + 1));
    dots.forEach((dot, dotIndex) => dot.addEventListener('click', () => move(dotIndex)));

    slider.addEventListener('mouseenter', stop);
    slider.addEventListener('mouseleave', start);
    slider.addEventListener('focusin', stop);
    slider.addEventListener('focusout', (event) => {
      if (!slider.contains(event.relatedTarget)) start();
    });
    slider.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') move(active - 1);
      if (event.key === 'ArrowRight') move(active + 1);
    });
    slider.addEventListener('pointerdown', (event) => {
      pointerStart = event.clientX;
    });
    slider.addEventListener('pointerup', (event) => {
      if (pointerStart === null) return;
      const distance = event.clientX - pointerStart;
      pointerStart = null;
      if (Math.abs(distance) < 48) return;
      move(active + (distance < 0 ? 1 : -1));
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop();
      else start();
    });

    show(active, true);
    start();
  };

  initAdCampaigns();
  document.querySelectorAll('[data-lab-slider]').forEach(initSlider);

  document.addEventListener('shopify:section:load', (event) => {
    initPredictiveSearches(event.target);
    initAdCampaigns(event.target);
    event.target.querySelectorAll('[data-lab-slider]').forEach(initSlider);
    initSideFeeds(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    predictiveSearchRootsIn(event.target).forEach((root) => root.__labPredictiveCleanup?.());
  });

  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-lab-slide]');
    const slider = slide?.closest('[data-lab-slider]');
    if (!slide || !slider || typeof slider.__labSliderShow !== 'function') return;
    slider.__labSliderShow(Number(slide.dataset.labSlideIndex) || 0);
  });
})();
